import React from 'react';
import { useHistory } from 'react-router-dom';
import { pathManager } from 'routes';
import { useOpenModal } from 'utils/modals/useOpenModal';

import IntakeIcon from '@mui/icons-material/AssignmentOutlined';
import RemoveIcon from '@mui/icons-material/HighlightOffOutlined';
import MailIcon from '@mui/icons-material/Mail';
import PersonAddIcon from '@mui/icons-material/PersonAddOutlined';
import HireIcon from '@mui/icons-material/WorkOutlineOutlined';

import { JobMatchStageEnum } from '@libs/graphql-types';

import {
  ActionOptionType,
  NewActionsBlockProps,
  NewActionsBlockModals,
} from './types';

export const useChildMenuAction = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const open = Boolean(anchorEl);

  const onClick = (event: any) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return {
    open,
    handleClose,
    anchorEl,
    onClick,
  };
};

export const useMenuActions = ({
  jobId,
  jobMatch,
}: NewActionsBlockProps): ActionOptionType[] => {
  const openConfirmStageChangeModal = useOpenModal<{
    jobMatchId: string;
    targetStage: JobMatchStageEnum;
  }>(NewActionsBlockModals.CONFIRM_STAGE_CHANGE);
  const history = useHistory();

  const rejectAction = {
    text: 'Reject',
    Icon: RemoveIcon,
    onClick: () =>
      openConfirmStageChangeModal({
        jobMatchId: jobMatch.id,
        targetStage: JobMatchStageEnum.Rejected,
      }),
  };

  const internalInterviewAction = {
    text: 'Internal interview',
    Icon: PersonAddIcon,
    onClick: () => {
      openConfirmStageChangeModal({
        jobMatchId: jobMatch.id,
        targetStage: JobMatchStageEnum.InternalInterview,
      });
    },
  };

  // Map of actions per stage
  const actionsMap: Record<JobMatchStageEnum, ActionOptionType[]> = {
    [JobMatchStageEnum.InstantMatch]: [internalInterviewAction, rejectAction],
    [JobMatchStageEnum.Application]: [internalInterviewAction, rejectAction],
    [JobMatchStageEnum.InternalInterview]: [
      {
        text: 'VMS Submit',
        Icon: MailIcon,
        onClick: () => {
          history.push(
            pathManager.company.applicationForm.generatePath({
              jobId: jobId,
              matchId: jobMatch.id,
            }),
          );
        },
        // openConfirmStageChangeModal({
        //   jobMatchId: jobMatch.id,
        //   targetStage: JobMatchStageEnum.Submittal,
        // }),
      },
      rejectAction,
    ],
    [JobMatchStageEnum.Submittal]: [
      {
        text: 'Shortlist',
        Icon: PersonAddIcon,
        onClick: () =>
          openConfirmStageChangeModal({
            jobMatchId: jobMatch.id,
            targetStage: JobMatchStageEnum.ShortList,
          }),
      },
      rejectAction,
    ],
    [JobMatchStageEnum.ShortList]: [
      {
        text: 'Client interview',
        Icon: PersonAddIcon,
        onClick: () =>
          openConfirmStageChangeModal({
            jobMatchId: jobMatch.id,
            targetStage: JobMatchStageEnum.ClientInterview,
          }),
      },
      rejectAction,
    ],
    [JobMatchStageEnum.ClientInterview]: [
      {
        text: 'Offer',
        Icon: HireIcon,
        onClick: () =>
          openConfirmStageChangeModal({
            jobMatchId: jobMatch.id,
            targetStage: JobMatchStageEnum.Offer,
          }),
      },
      rejectAction,
    ],
    [JobMatchStageEnum.Offer]: [
      {
        text: 'Onboarding',
        Icon: IntakeIcon,
        onClick: () =>
          openConfirmStageChangeModal({
            jobMatchId: jobMatch.id,
            targetStage: JobMatchStageEnum.Onboarding,
          }),
      },
      rejectAction,
    ],
    [JobMatchStageEnum.Onboarding]: [
      {
        text: 'Placement',
        Icon: HireIcon,
        onClick: () =>
          openConfirmStageChangeModal({
            jobMatchId: jobMatch.id,
            targetStage: JobMatchStageEnum.Placement,
          }),
      },
      rejectAction,
    ],
    [JobMatchStageEnum.Placement]: [rejectAction],
    [JobMatchStageEnum.Rejected]: [],
  };

  const stage =
    (jobMatch.stage as JobMatchStageEnum) || JobMatchStageEnum.InstantMatch;
  return actionsMap[stage] || [];
};
